import { ActionTree } from 'vuex'
import { ApiVersion, RootState } from '@/store/types'
import { ConsultoryMutations } from '@/store/consultories/mutations'
import axios from 'axios'
import router from '@/router'
import {
  ConsultoryCreatePayload,
  ConsultoryState,
  DoctorConsultoryRequest,
  ConsultoryItem,
  ModulesResponse,
  ModuleRequest,
  ModuleDoctorItem,
  AddDoctorRequest,
  ModuleScheduleItem,
  DayScheduleRequestCreate,
  LoadingScheduleDay,
  EnableDayRequest,
  EventItem,
  SuscribeEventRequest,
  EventRequest,
  UnsuscribeEventRequest
} from '@/store/consultories/types'
import {
  NotificationType,
  Position,
  showNotification
} from '@/utils/Notification'
import { Response } from '@/store/response'
import { Doctor } from '@/store/doctor/types'

export const actions: ActionTree<ConsultoryState, RootState> = {
  //Create consultory
  async createConsultory(
    { commit },
    doctorPayload: ConsultoryCreatePayload<DoctorConsultoryRequest>
  ) {
    try {
      commit(ConsultoryMutations.SET_LOADER_CREATE_CONSULTORY, true)
      const { data } = await axios.post(
        `${ApiVersion.V1}/Consultories`,
        doctorPayload.payload
      )
      const response: Response<String> = data
      router.push({
        path: '/consultory-sent',
        query: { folio: response.data?.toString() }
      })
    } catch (error) {
      console.error(error)
    } finally {
      commit(ConsultoryMutations.SET_LOADER_CREATE_CONSULTORY, false)
    }
  },

  //Get doctor consultories list
  async getConsultoriesList({ commit }) {
    try {
      commit(ConsultoryMutations.SET_LOADER_CONSULTORIES_LIST, true)
      const { data } = await axios.get(`${ApiVersion.V1}/Consultories`)
      const response: Response<Array<ConsultoryItem>> = data
      commit(ConsultoryMutations.SET_PROFILE_CONSULTORIES, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(ConsultoryMutations.SET_LOADER_CONSULTORIES_LIST, false)
    }
  },

  //Get doctor modules list by consultory
  async getModulesInConsultory({ commit }, payload: ModuleRequest) {
    try {
      commit(ConsultoryMutations.SET_LOADER_MODULES, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Consultories/${payload.consultoryId}/modules`
      )
      const response: Response<ModulesResponse> = data
      commit(ConsultoryMutations.SET_MODULES_LIST, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(ConsultoryMutations.SET_LOADER_MODULES, false)
    }
  },

  //Get doctor modules list
  async getDoctorsInModule({ commit }, moduleId: number) {
    try {
      commit(ConsultoryMutations.SET_LOADER_DOCTORS_LIST, false)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Consultories/modules/${moduleId}/doctors`
      )
      const response: Response<Array<ModuleDoctorItem>> = data
      commit(ConsultoryMutations.SET_DOCTORS_LIST, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(ConsultoryMutations.SET_LOADER_DOCTORS_LIST, false)
    }
  },

  // Add doctor to module
  async addDoctorToModule({ dispatch }, request: AddDoctorRequest) {
    try {
      let config = {
        headers: {
          doctorId: request.doctorId
        }
      }
      await axios.post(
        `${ApiVersion.V1}/Consultories/modules/${request.moduleId}`,
        null,
        config
      )
      dispatch('getDoctorsInModule', request.moduleId)
    } catch (error) {
      console.error(error)
    }
  },

  // Search doctor by credential
  async searchDoctorByCredential({ commit }, credential: number) {
    try {
      commit(ConsultoryMutations.SET_LOADER_SEARCH_DOCTOR, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Doctors/search/${credential}`
      )
      const response: Response<Doctor> = data
      commit(ConsultoryMutations.SET_DOCTOR_SEARCH, response.data)
    } catch (error) {
      console.error(error)
      showNotification(
        NotificationType.WARNING,
        'No se encontró el doctor',
        2000,
        Position.TOP_RIGHT
      )
    } finally {
      commit(ConsultoryMutations.SET_LOADER_SEARCH_DOCTOR, false)
    }
  },

  //Get module's schedule by id
  async getModuleScheduleById({ commit }, moduleId: number) {
    try {
      commit(ConsultoryMutations.SET_MODULE_SCHEDULE_LOADER, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Consultories/modules/${moduleId}/schedule/configs`
      )
      const response: Response<Array<ModuleScheduleItem>> = data
      commit(ConsultoryMutations.SET_MODULE_SCHEDULE, response.data)
      console.log(response.data)
    } catch (error) {
      console.error(error)
      commit(ConsultoryMutations.SET_MODULE_SCHEDULE, [])
    } finally {
      commit(ConsultoryMutations.SET_MODULE_SCHEDULE_LOADER, false)
    }
  },

  async addModuleDaySchedule({ commit }, request: DayScheduleRequestCreate) {
    const loadingDay: LoadingScheduleDay = {
      dayNumber: request.request.dayOfWeek,
      loading: false
    }

    try {
      commit(
        ConsultoryMutations.SET_LOADER_SCHEDULE_DAY,
        (loadingDay.loading = true)
      )
      await axios.post(
        `${ApiVersion.V1}/Consultories/modules/${request.moduleId}/schedule/configs`,
        request.request
      )

      commit(
        ConsultoryMutations.SET_LOADER_SCHEDULE_DAY,
        (loadingDay.loading = false)
      )
      showNotification(NotificationType.SUCCESS, 'Horario establecido', 1000)
    } catch (error) {
      console.error(error)
    } finally {
      commit(
        ConsultoryMutations.SET_LOADER_SCHEDULE_DAY,
        (loadingDay.loading = false)
      )
    }
  },

  async enableDaySchedule({ commit }, request: EnableDayRequest) {
    const loadingDay: LoadingScheduleDay = {
      dayNumber: request.dayNumber,
      loading: false
    }

    try {
      commit(
        ConsultoryMutations.SET_LOADER_SCHEDULE_DAY,
        (loadingDay.loading = true)
      )

      const { data } = await axios.post(
        `${ApiVersion.V1}/Consultories/modules/${request.moduleId}/schedule/configs/enable-day/${request.dayNumber}/${request.enabled}`
      )
      const response: Response<String> = data
      showNotification(NotificationType.SUCCESS, `${response.message}`, 2000)
      commit(
        ConsultoryMutations.SET_LOADER_SCHEDULE_DAY,
        (loadingDay.loading = false)
      )
    } catch (error) {
      console.error(error)
    } finally {
      commit(
        ConsultoryMutations.SET_LOADER_SCHEDULE_DAY,
        (loadingDay.loading = false)
      )
    }
  },

  // Module's events
  async getModuleEvents({ commit }, request: EventRequest) {
    try {
      commit(ConsultoryMutations.SET_LOADER_MODULE_EVENTS, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Consultories/modules/${request.moduleId}/schedule/events?year=${request.year}&weeknumber=${request.weekNumber}`
      )
      let response: Response<Array<EventItem>> = data
      console.log(response.data)
      if (request.weekNumber == 1) {
        const resp = await axios.get(
          `${ApiVersion.V1}/Consultories/modules/${
            request.moduleId
          }/schedule/events?year=${request.year - 1}&weeknumber=${53}`
        )
        response.data = response.data?.concat(resp.data.data)
      }

      commit(ConsultoryMutations.SET_MODULE_EVENTS, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(ConsultoryMutations.SET_LOADER_MODULE_EVENTS, false)
    }
  },

  //Suscribe to event
  async suscribeEvent(_, request: SuscribeEventRequest) {
    try {
      await axios.post(
        `${ApiVersion.V1}/Consultories/modules/${request.moduleId}/schedule/events/suscribe/${request.scheduleEventId}/${request.doctorId}`
      )
    } catch (error) {
      console.error(error)
    }
  },

  //Unuscribe to event
  async unsuscribeEvent(_, request: UnsuscribeEventRequest) {
    try {
      await axios.post(
        `${ApiVersion.V1}/Consultories/modules/${request.moduleId}/schedule/events/unsuscribe/${request.scheduleEventId}/${request.doctorId}/${request.type}`
      )
    } catch (error) {
      console.error(error)
    }
  }
}
